import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { ProductType } from '@apps/api-interfaces';

// WebMCP (Web Model Context Protocol, propozycja W3C WebML WG):
// strona rejestruje narzedzia w `document.modelContext` (Chrome 150+;
// wczesniejszy punkt wejscia `navigator.modelContext` jako fallback),
// agent dzialajacy w przegladarce wywoluje je zamiast klikac po DOM.
// Produkcyjnie API wlacza token origin trial w index.html (Chrome 149-156).
//
// GRANICA AUTOMATYZACJI (celowa): agent moze przejsc caly flow porownywarki
// (produkt -> kod pocztowy -> dostawca -> zuzycie -> porownanie -> oferta),
// ale NIE ISTNIEJE narzedzie wysylajace formularz kontaktowy - dane
// kontaktowe, zgody i faktury uzupelnia CZLOWIEK na widoku szczegolow,
// do ktorego prowadzi `przejdz_do_kontaktu`.
//
// API feature-detect: obslugujemy zarowno `provideContext({tools})` jak
// i inkrementalne `registerTool` (kszalt draftu zmienia sie - przy braku
// wsparcia serwis jest no-opem bez bledow).

const PRODUCTS: ProductType[] = ['prad', 'gaz', 'fotowoltaika'];
const ZIP_RE = /^\d{2}-\d{3}$/;

export interface McpOffer {
  id: string;
  nazwa: string;
  cena?: string;
  oszczednosc?: string;
  wyrozniona?: boolean;
}

interface CalcState {
  product?: ProductType;
  zip?: string;
  provider?: string;
  value?: number;
}

const textResult = (text: string) => ({
  content: [{ type: 'text', text }]
});

@Injectable({ providedIn: 'root' })
export class WebMcpService {
  private state: CalcState = {};
  private offers: McpOffer[] = [];
  private registered = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private zone: NgZone
  ) {}

  /** Komponent porownania zglasza aktualna liste ofert (dla `lista_ofert`). */
  setOffers(offers: McpOffer[]) {
    this.offers = offers || [];
  }

  init() {
    if (!isPlatformBrowser(this.platformId) || this.registered) {
      return;
    }

    const mc =
      (document as any).modelContext || (navigator as any).modelContext;
    if (!mc) {
      return; // przegladarka bez WebMCP - zero side effectow
    }

    const tools = this.buildTools();

    try {
      if (typeof mc.provideContext === 'function') {
        mc.provideContext({ tools });
      } else if (typeof mc.registerTool === 'function') {
        tools.forEach((tool: any) => mc.registerTool(tool));
      } else {
        return;
      }
      this.registered = true;
    } catch (e) {
      // Niekompatybilny draft API - nie wywracamy aplikacji.
      console.warn('[WebMCP] rejestracja narzedzi nie powiodla sie', e);
    }
  }

  private buildTools(): any[] {
    return [
      {
        name: 'wybierz_produkt',
        description:
          'Wybiera rodzaj porownywarki energii: prad, gaz lub fotowoltaika. ' +
          'Selects the energy comparison type.',
        inputSchema: {
          type: 'object',
          properties: {
            produkt: { type: 'string', enum: PRODUCTS }
          },
          required: ['produkt']
        },
        execute: async (input: any) => {
          const produkt = input && input.produkt;
          if (PRODUCTS.indexOf(produkt) === -1) {
            return textResult(
              'Nieznany produkt. Dozwolone: prad, gaz, fotowoltaika.'
            );
          }
          this.state.product = produkt;
          await this.navigate(['/', produkt]);
          return textResult(`Wybrano porownywarke: ${produkt}.`);
        }
      },
      {
        name: 'ustaw_kod_pocztowy',
        description:
          'Ustawia kod pocztowy lokalizacji firmy (format NN-NNN). ' +
          'Sets the postal code used to match available offers.',
        inputSchema: {
          type: 'object',
          properties: { kod: { type: 'string', pattern: '^\\d{2}-\\d{3}$' } },
          required: ['kod']
        },
        execute: async (input: any) => {
          const kod = (input && input.kod) || '';
          if (!ZIP_RE.test(kod)) {
            return textResult(
              'Niepoprawny kod pocztowy - oczekiwany format NN-NNN, np. 00-001.'
            );
          }
          this.state.zip = kod;
          return textResult(`Kod pocztowy ustawiony: ${kod}.`);
        }
      },
      {
        name: 'ustaw_dostawce',
        description:
          'Ustawia obecnego sprzedawce energii/gazu (np. pge, tauron, enea, energa, innogy, inny). ' +
          'Sets the current provider.',
        inputSchema: {
          type: 'object',
          properties: { dostawca: { type: 'string' } },
          required: ['dostawca']
        },
        execute: async (input: any) => {
          const dostawca = ((input && input.dostawca) || '')
            .toLowerCase()
            .trim();
          if (!dostawca) {
            return textResult('Podaj nazwe obecnego dostawcy.');
          }
          this.state.provider = dostawca;
          return textResult(`Obecny dostawca ustawiony: ${dostawca}.`);
        }
      },
      {
        name: 'ustaw_zuzycie',
        description:
          'Ustawia roczne zuzycie: dla pradu w MWh/rok, dla gazu w MWh/rok, ' +
          'dla fotowoltaiki roczny koszt pradu w zl. Sets yearly consumption.',
        inputSchema: {
          type: 'object',
          properties: { wartosc: { type: 'number', minimum: 1 } },
          required: ['wartosc']
        },
        execute: async (input: any) => {
          const wartosc = Number(input && input.wartosc);
          if (!wartosc || wartosc <= 0) {
            return textResult('Zuzycie musi byc liczba wieksza od zera.');
          }
          this.state.value = wartosc;
          return textResult(`Zuzycie ustawione: ${wartosc}.`);
        }
      },
      {
        name: 'pokaz_porownanie',
        description:
          'Otwiera porownanie ofert dla ustawionych wczesniej parametrow ' +
          '(produkt, kod pocztowy, dostawca, zuzycie). Opens the offer comparison.',
        inputSchema: { type: 'object', properties: {} },
        execute: async () => {
          const missing = [];
          if (!this.state.product) missing.push('produkt (wybierz_produkt)');
          if (!this.state.zip)
            missing.push('kod pocztowy (ustaw_kod_pocztowy)');
          if (missing.length) {
            return textResult(`Brakuje danych: ${missing.join(', ')}.`);
          }
          const queryParams: any = { zip: this.state.zip };
          if (this.state.provider) queryParams.provider = this.state.provider;
          if (this.state.value) queryParams.value = this.state.value;
          await this.navigate(
            ['/', this.state.product, 'kalkulator', 'porownanie'],
            queryParams
          );
          return textResult(
            'Otwarto porownanie ofert. Uzyj lista_ofert, aby pobrac wyniki.'
          );
        }
      },
      {
        name: 'lista_ofert',
        description:
          'Zwraca liste ofert z otwartego porownania (nazwa, cena, oszczednosc). ' +
          'Ceny sa szacunkowe. Returns offers from the open comparison view.',
        inputSchema: { type: 'object', properties: {} },
        execute: async () => {
          if (!this.offers.length) {
            return textResult(
              'Brak zaladowanych ofert - najpierw wywolaj pokaz_porownanie i poczekaj na wyniki.'
            );
          }
          return textResult(
            JSON.stringify(
              {
                disclaimer:
                  'Ceny szacunkowe na podstawie podanych danych; ostateczna oferta po kontakcie.',
                oferty: this.offers.slice(0, 20)
              },
              null,
              1
            )
          );
        }
      },
      {
        name: 'wybierz_oferte',
        description:
          'Wybiera oferte z porownania po jej id (z lista_ofert) i przechodzi ' +
          'do kroku szczegolow. Selects an offer by id.',
        inputSchema: {
          type: 'object',
          properties: { id: { type: 'string' } },
          required: ['id']
        },
        execute: async (input: any) => {
          const id = input && input.id;
          const offer = this.offers.filter(o => o.id === id)[0];
          if (!offer) {
            return textResult(
              'Nie znaleziono oferty o podanym id - uzyj lista_ofert.'
            );
          }
          await this.navigate(
            ['/', this.state.product || 'prad', 'kalkulator', 'detale'],
            { offer: offer.id, zip: this.state.zip, value: this.state.value }
          );
          return textResult(
            `Wybrano oferte "${offer.nazwa}". Dalsze kroki (dane kontaktowe, zgody, faktury) uzupelnia uzytkownik na stronie.`
          );
        }
      },
      {
        name: 'przejdz_do_kontaktu',
        description:
          'Otwiera widok kontaktu/finalizacji. KONIEC AUTOMATYZACJI: dane ' +
          'kontaktowe, zgody RODO i faktury musi uzupelnic uzytkownik ' +
          'bezposrednio na stronie - nie ma narzedzia do wysylki formularza. ' +
          'Human handoff: the user completes contact data on the page.',
        inputSchema: { type: 'object', properties: {} },
        execute: async () => {
          await this.navigate(
            ['/', this.state.product || 'prad', 'kalkulator', 'detale'],
            this.state.zip
              ? { zip: this.state.zip, value: this.state.value }
              : {}
          );
          return textResult(
            'Otwarto widok kontaktu. Przekaz uzytkownikowi, ze dalsze pola ' +
              '(telefon, zgody, faktury) uzupelnia samodzielnie na stronie.'
          );
        }
      }
    ];
  }

  private navigate(commands: any[], queryParams: any = {}): Promise<boolean> {
    // Wywolania przychodza spoza strefy Angulara (agent) - wracamy do zone,
    // inaczej change detection nie zauwazy nawigacji.
    return this.zone.run(() => this.router.navigate(commands, { queryParams }));
  }
}
